import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Paper,
  CircularProgress,
  Backdrop
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { BASE_URL } from "../../variables/api";

const DetailedFormView = (props) => {
  const navigate = useNavigate();
  const { id, questionId, questionName } = useParams();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.post(`${BASE_URL}/surveys/getresponses.php`, { id: id })
      .then((res) => {
        if (res.data.data.length === 0) {
          Swal.fire({
            title: "No submitted responses available for this form yet",
            confirmButtonText: `ok`,
          }).then((result) => {
            if (result.isConfirmed) {
              navigate(`/`);
            }
          });
        } else {
          setResponses(res.data.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching responses:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, navigate]);

  return (
    <Box sx={{ p: { xs: 0, sm: 1 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Loading Overlay */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Loading Responses...</Typography>
        </Box>
      </Backdrop>

      <Button
        onClick={() => navigate(-1)}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 3 }}
        variant="outlined"
      >
        Back
      </Button>

      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        Detailed View: {questionName}
      </Typography>

      <Paper elevation={3} sx={{ p: 2 }}>
        <List>
          {responses.map((responseArr, index) => {
            // Find the specific question in this response array
            const questionDetails = responseArr.find(q => q.id === questionId);

            if (!questionDetails) return null;

            return (
              <Fragment key={index}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" fontWeight="bold">
                        Response {index + 1}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block', mt: 1 }}>
                        {/* Render answer based on type */}
                        {(() => {
                          const type = questionDetails.type.value;
                          
                          if (type === "text" || type === "paragraph" || type === "email" || type === "number" || type === "date") {
                            return <Typography variant="body1">{questionDetails.response || "No answer"}</Typography>;
                          } else if (type === "select") {
                            return <Chip label={questionDetails.response?.value || questionDetails.response || "No selection"} color="primary" variant="outlined" />;
                          } else if (type === "checkbox" || type === "radio" || type === "linescale") {
                            const selectedOptions = questionDetails.options.filter(o => o.response === true);
                            if (selectedOptions.length === 0) return "No selection";
                            return (
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {selectedOptions.map((opt, i) => (
                                  <Chip key={i} label={opt.value} color="primary" variant="outlined" />
                                ))}
                              </Box>
                            );
                          } else if (type === "file") {
                             if (!questionDetails.response) return "No file";
                             try {
                               const fileData = typeof questionDetails.response === 'string' ? JSON.parse(questionDetails.response) : questionDetails.response;
                               return <Typography variant="body2" color="primary">{fileData.fileName}</Typography>;
                             } catch (e) {
                               return "File uploaded";
                             }
                          }
                          return JSON.stringify(questionDetails.response);
                        })()}
                      </Box>
                    }
                  />
                </ListItem>
                {index < responses.length - 1 && <Divider component="li" />}
              </Fragment>
            );
          })}
        </List>
      </Paper>
    </Box>
  );
};

export default DetailedFormView;


